import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { ThemeProvider } from "./components/theme-provider";
import { SentryUserSync } from "./components/SentryUserSync";
import { initErrorReporting } from "./lib/error-reporting";
import { loadEnv } from "./lib/env";
import App from "./App.tsx";
import "./index.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

initErrorReporting();

async function boot(): Promise<void> {
  let env: ReturnType<typeof loadEnv>;
  try {
    env = loadEnv();
  } catch (err) {
    // Fail-fast: render a minimal pre-mount error and stop.
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = `
        <main role="alert" style="padding:2rem;font-family:system-ui">
          <h1 style="font-size:1.25rem;margin:0 0 0.5rem">Configuração inválida</h1>
          <pre style="white-space:pre-wrap;font-size:0.875rem;background:#1a1a1a;color:#f5f5f5;padding:1rem;border-radius:8px">${
            err instanceof Error ? err.message : String(err)
          }</pre>
          <p style="margin-top:1rem;font-size:0.875rem;color:#888">
            Verifique o arquivo <code>.env</code> contra <code>.env.example</code>
            (docs/architecture/data-layer-spec.md §10).
          </p>
        </main>
      `;
    }
    return;
  }

  // Start MSW worker conditionally:
  // - dev with mock adapter: always
  // - prod with mock adapter: only when VITE_ENABLE_MSW_IN_PROD=true
  const enableMsw =
    env.VITE_DATA_ADAPTER === "mock" &&
    (import.meta.env.DEV || env.VITE_ENABLE_MSW_IN_PROD === "true");

  if (enableMsw) {
    const { startMockWorker } = await import("./mocks/browser");
    await startMockWorker();
  }

  const root = document.getElementById("root");
  if (!root) return;

  // ClerkProvider must always wrap the tree: every component that calls
  // useUser() / useClerk() will throw if there is no provider in context.
  // When AUTH_ENABLED=false we still need the provider — but Clerk hooks
  // won't actually be exercised because useCurrentUser() returns MOCK_USER
  // before checking isLoaded.
  // Fallback key is a valid-format key (pk_test_ + base64("example.com$"))
  // that Clerk accepts without crashing. It will fail to reach the API but
  // will never block rendering in mock/demo/CI mode.
  const clerkKey = PUBLISHABLE_KEY || "pk_test_ZXhhbXBsZS5jb20k";

  createRoot(root).render(
    <ClerkProvider
      publishableKey={clerkKey}
      signInUrl="/login"
      signUpUrl="/register"
      signInForceRedirectUrl="/"
      signUpForceRedirectUrl="/"
    >
      <ThemeProvider defaultTheme="dark" storageKey="codigo-poder-theme">
        <SentryUserSync />
        <App />
      </ThemeProvider>
    </ClerkProvider>,
  );
}

void boot();
